import { EASING_BEZIER_OUT_CUBIC } from 'compose/CompositionEffect';
import BackgroundStyle from 'data/BackgroundStyle';
import { CaptionsToken } from 'data/CaptionsToken';
import { CaptionsFrameData, FrameData, TextFrameData } from 'data/FlyerData';
import { Frame } from 'data/Frame';
import { FrameType } from 'data/FrameType';
import { TextFrame } from 'data/TextFrame';
import { TextTransform } from 'data/TextTransform';
import { getCloneId } from 'utils';
import { addTextBackground, splitTextIntoWords } from 'utils/PixiUtils';
import { CaptionsDisplayMode } from './CaptionsDisplayMode';

const MIN_GAP_BETWEEN_PAGES = 2;

const PUNCTUATIONS = [',', ';', ':', '.', '?', '!'];
const ANIMATION_DURATION = 0.35;

const isSentenceEnd = (text: string): boolean => {
  if (text.length < 2) {
    return false;
  }
  const lastLetter = text.charAt(text.length - 1);
  return PUNCTUATIONS.includes(lastLetter);
};

export class CaptionsFrame extends TextFrame {
  type = FrameType.Captions;
  displayMode = CaptionsDisplayMode.Pages;
  pages: CaptionsToken[] = [];
  visiblePageIndex = -1;
  lastTime = 0;
  private initialYs: number[] = [];

  private _textSource: PIXI.Text;
  private _container = new PIXI.Container();
  private _wordsContainer = new PIXI.Container();
  private _backgroundContainer = new PIXI.Container();
  private _tokens: CaptionsToken[] = [];
  get tokens(): CaptionsToken[] {
    return this._tokens;
  }
  set tokens(tokens: CaptionsToken[]) {
    this._tokens = tokens;
    this.rebuild();
    this.clear();
    this.update(this.lastTime);
    this.emit('update'); // unfortunately this doesn't work because the frame is not selected anymore and it won't trigger the rendering.
  }

  private _hasBackground = false;
  get hasBackground(): boolean {
    return this._hasBackground;
  }
  set hasBackground(value: boolean) {
    this._hasBackground = value;
    if (value) {
      this.rebuild();
    } else {
      this._backgroundContainer.visible = false;
    }
  }

  private _backgroundColor: string = '#00000066';
  get backgroundColor(): string {
    return this._backgroundColor;
  }
  set backgroundColor(value: string) {
    this._backgroundColor = value;
    if (
      this._backgroundContainer.visible &&
      this._backgroundContainer.children.length
    ) {
      this._backgroundContainer.removeChildren();
      this.buildBackgrounds();
    }
  }

  private _animated: boolean = false;
  get animated(): boolean {
    return (
      [
        CaptionsDisplayMode.ComposeAll,
        CaptionsDisplayMode.ComposePages
      ].includes(this.displayMode) && this._animated
    );
  }
  set animated(value: boolean) {
    this._animated = value;
    this.rebuild();
    this.clear();
    this.update(this.lastTime);
    this.emit('update');
  }

  get canRotate(): boolean {
    return false;
  }

  generateThumbnail() {
    const textCopy = new PIXI.Text('CC', this.textStyle);
    (textCopy as any).updateText();
    this.generateThumbnailFromSource(textCopy.texture.baseTexture.source);
    textCopy.destroy();
  }

  updateThumbnail() {
    if (this.hasBackground) {
      this.generateThumbnail();
      this.backgroundStyle = BackgroundStyle.Transparent;
      this.emit('edited');
    } else {
      super.updateThumbnail();
    }
  }

  generateThumbnailFromSource(source: any): void {
    if (this.thumbnailURL) {
      URL.revokeObjectURL(this.thumbnailURL);
    }
    const THUMBNAIL_SIZE: number = 120;
    const canvas = document.createElement('canvas');
    canvas.width = THUMBNAIL_SIZE;
    canvas.height = THUMBNAIL_SIZE;
    var ctx: CanvasRenderingContext2D = canvas.getContext('2d')!;

    const width = source.width;
    const height = source.height;

    const scale = THUMBNAIL_SIZE / Math.max(width, height);
    const w = width * scale;
    const h = height * scale;

    ctx.drawImage(
      source,
      (THUMBNAIL_SIZE - w) * 0.5,
      (THUMBNAIL_SIZE - h) * 0.5,
      w,
      h
    );
    if (this.hasBackground) {
      ctx.globalCompositeOperation = 'destination-over';
      ctx.fillStyle = this.backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    this.thumbnailURL = canvas.toDataURL('image/png');
  }

  didChangeStyle(property: string) {
    // console.log('CHANGE STYLE: ' + property);
    if (
      [
        'font',
        'uppercase',
        'font size',
        'line height',
        'alignment',
        'letter space'
      ].includes(property)
    ) {
      this.rebuild();
    } else {
      this._wordsContainer.children.forEach(child => {
        const wordView = child as PIXI.Text;
        (wordView as any).style = { ...this.textStyle };
      });
    }
    this.emit('update'); // triggers render
  }

  removedFromView = () => {
    this.clear();
  };

  clear() {
    switch (this.displayMode) {
      case CaptionsDisplayMode.Pages:
      case CaptionsDisplayMode.ComposePages:
        this.visiblePageIndex = -1;
        this.text = '';
        break;
      default:
      // do nothing
    }
  }

  rebuild() {
    this._wordsContainer.removeChildren();
    switch (this.displayMode) {
      case CaptionsDisplayMode.AllVisible:
        this.buildAllVisible();
        this._textSource.visible = true;
        break;
      case CaptionsDisplayMode.Pages:
        this.buildPages();
        this._textSource.visible = true;
        break;
      case CaptionsDisplayMode.ComposeAll:
        this.buildWords();
        this._textSource.visible = false;
        break;
      case CaptionsDisplayMode.ComposePages:
        this.buildPages();
        this._textSource.visible = false;
        break;
      default:
        console.log('NO DISPLAY MODE SET: ' + this.displayMode);
    }
  }

  fitsInBox(text: string): boolean {
    const uppercase =
      (this.textStyle as any).textTransform === TextTransform.Uppercase;
    if (uppercase) {
      text = text.toUpperCase();
    }
    const metrics = PIXI.TextMetrics.measureText(text, this.textStyle!);
    return (
      metrics.width < this._boundingBox.width &&
      metrics.height < this._boundingBox.height
    );
  }

  buildPages() {
    this.visiblePageIndex = -1;
    this.pages = [];
    if (!this.tokens.length) {
      return;
    }
    const pool = [...this.tokens];
    let token = pool.shift()!;
    let currentPage: CaptionsToken = {
      ...token
    };
    this.pages.push(currentPage);
    while (pool.length) {
      while (
        !isSentenceEnd(currentPage.value) &&
        pool.length &&
        pool[0].start < currentPage.end + MIN_GAP_BETWEEN_PAGES &&
        this.fitsInBox(`${currentPage.value} ${pool[0].value}`)
      ) {
        token = pool.shift()!;
        currentPage.value += ` ${token.value}`;
        currentPage.end = token.end;
      }
      if (pool.length) {
        token = pool.shift()!;
        currentPage = {
          ...token
        };
        this.pages.push(currentPage);
      }
    }
    // console.log('Pages:');
    // console.log(this.pages);
    this.updatePage(this.lastTime);
  }

  buildBackgrounds() {
    addTextBackground(
      this._backgroundContainer,
      this.textStyle,
      this._boundingBox,
      this.textMetrics,
      this._backgroundColor
    );
    this._backgroundContainer.visible = true;
  }

  populateWordsYs() {
    this.initialYs = [];
    this._wordsContainer.children.forEach((child, index) => {
      this.initialYs[index] = child.y;
    });
  }

  buildWords() {
    this.buildAllVisible();
    splitTextIntoWords(
      this._wordsContainer,
      this.text,
      this.textStyle,
      this.boundingBox
    );
    if (this.animated) {
      this.populateWordsYs();
    }
    let tokens = [...this._tokens];
    let currentToken = tokens.shift();
    let currentText = currentToken!.value;
    this._wordsContainer.children.forEach(child => {
      const wordView = child as PIXI.Text;
      (wordView as any).token = currentToken;
      currentText = currentText.substring(wordView.text.length).trim();
      if (currentText.length === 0 && tokens.length) {
        currentToken = tokens.shift();
        currentText = currentToken!.value;
      }
    });
  }

  buildPageWords(page: CaptionsToken) {
    splitTextIntoWords(
      this._wordsContainer,
      this.text,
      this.textStyle,
      this.boundingBox
    );
    if (this.animated) {
      this.populateWordsYs();
    }
    let tokens = [...this._tokens];
    const startingToken = this._tokens.find(token => token.key === page.key);
    // console.log('starting token: ');
    // console.log(startingToken);
    if (!startingToken) {
      console.error('Could not find matching token');
      return;
    }
    const startingIndex = this._tokens.indexOf(startingToken);
    let i = startingIndex;
    let currentToken = startingToken;
    let currentText = currentToken!.value;
    this._wordsContainer.children.forEach(child => {
      const wordView = child as PIXI.Text;
      (wordView as any).token = currentToken;
      currentText = currentText.substring(wordView.text.length).trim();
      if (currentText.length === 0 && i < tokens.length - 1) {
        i++;
        currentToken = this._tokens[i];
        currentText = currentToken!.value;
      }
    });
  }

  buildAllVisible() {
    let text = '';
    this.tokens.forEach(token => {
      text += token.value + ' ';
    });
    this.setText(text);
  }

  get boundingBox(): PIXI.Rectangle {
    return this._boundingBox;
  }
  set boundingBox(value: PIXI.Rectangle) {
    this._boundingBox = value;
    if (value) {
      this.textStyle.wordWrapWidth = value.width;
      this.textMetrics = PIXI.TextMetrics.measureText(
        this.text,
        this.textStyle!
      );
      this.rebuild();
    }
  }

  showWordsUntilTime(time: number) {
    if (this._animated) {
      this._wordsContainer.children.forEach((child, index) => {
        const token = (child as any).token as CaptionsToken;
        let phase = 0;
        if (time > token.start) {
          phase = Math.min(1, (time - token.start) / ANIMATION_DURATION);
        }
        const p = EASING_BEZIER_OUT_CUBIC(phase);
        child.alpha = p;
        child.y =
          this.initialYs[index] + (1 - p) * this.textStyle.lineHeight * 0.33;
      });
    } else {
      this._wordsContainer.children.forEach(child => {
        const token = (child as any).token as CaptionsToken;
        child.visible = token.start < time;
      });
    }
  }

  update(time: number) {
    this.lastTime = time;
    switch (this.displayMode) {
      case CaptionsDisplayMode.Pages:
        this.updatePage(time);
        break;
      case CaptionsDisplayMode.ComposePages:
        this.updatePage(time);
        this.showWordsUntilTime(time);
        break;
      case CaptionsDisplayMode.ComposeAll:
        this.showWordsUntilTime(time);
        break;
      default:
      // do nothing
    }
  }

  setText(text: string) {
    const uppercase =
      (this.textStyle as any).textTransform === TextTransform.Uppercase;
    if (uppercase) {
      text = text.toUpperCase();
    }
    this.text = text;
    if (this._hasBackground) {
      this._backgroundContainer.removeChildren();
      if (text.trim().length > 0) {
        this.buildBackgrounds();
      }
    }
  }

  updatePage(time: number) {
    let newVisiblePageIndex = -1;
    const extension = this.animated ? ANIMATION_DURATION * 0.5 : 0;
    for (let i = 0; i < this.pages.length; i++) {
      const page = this.pages[i];
      if (time >= page.start && time < page.end + extension) {
        newVisiblePageIndex = i;
        break;
      }
    }
    const wordByWord = this.displayMode === CaptionsDisplayMode.ComposePages;
    if (newVisiblePageIndex !== this.visiblePageIndex) {
      if (wordByWord) {
        this._wordsContainer.removeChildren();
      }
      if (newVisiblePageIndex > -1) {
        const page = this.pages[newVisiblePageIndex];
        this.setText(page.value);
        if (wordByWord) {
          this.buildPageWords(page);
        }
      } else {
        this.setText('');
      }
      this.visiblePageIndex = newVisiblePageIndex;
    }
  }

  protected get textSource(): PIXI.Text {
    return this._textSource;
  }

  populateWithData(data: FrameData) {
    super.populateWithData(data);
    this._textSource = this.sprite;
    this._container.addChild(this._backgroundContainer);
    this._container.addChild(this._textSource);
    this._container.addChild(this._wordsContainer);
    this.sprite = this._container;
    const captionsData = data as CaptionsFrameData;
    this.displayMode = captionsData.displayMode || this.displayMode;
    if (captionsData.backgroundColor) {
      this._hasBackground = true;
      this._backgroundColor = captionsData.backgroundColor;
    }
    if (captionsData.animated) {
      this._animated = captionsData.animated;
    }
    this.rebuild();
  }

  asData(forDatabase: boolean): FrameData {
    const result: CaptionsFrameData = {
      ...(super.asData(forDatabase) as TextFrameData),
      displayMode: this.displayMode
    };
    if (this.hasBackground) {
      result.backgroundColor = this._backgroundColor;
    }
    if (this.animated) {
      result.animated = true;
    }
    (result as any).text = null;
    return result;
  }

  dispose() {
    super.dispose();
    this._container.destroy(true);
  }

  clone(copyIndex: number): Frame {
    const clone = new CaptionsFrame(this.stageWidth, this.stageHeight);
    clone.id = getCloneId(this.id, copyIndex);
    clone._tokens = this.tokens;
    clone.populateWithData(this.asData(false));
    clone.backgroundStyle = this.backgroundStyle;
    return clone;
  }

  // private setSprite(sprite: PIXI.DisplayObject) {
  //   if (sprite === this.sprite) {
  //     return;
  //   }
  //   const parent = (this.sprite as PIXI.DisplayObject).parent;
  //   if (parent) {
  //     parent.removeChild(this.sprite);
  //   }
  //   this.sprite = sprite;
  //   if (parent) {
  //     parent.addChild(this.sprite);
  //   }
  // }
}



// WEBPACK FOOTER //
// ./src/captions/CaptionsFrame.ts